import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { FilterVal } from 'src/interfaces/iproptypes';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { findKeyByValue, isEmpty } from 'src/utils/functions';
import { COLOR_E } from 'src/const';
import LockSharpIcon from '@material-ui/icons/LockSharp';
import { ZoomButtons } from '../UtilityComponents/ZoomButtons';

const bedroomLabelToIndexMap = {
  Any: 100,
  Studio: 0,
  '1': 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '5': 5,
  '6+': 6,
  private_room: 'private_room',
  shared_room: 'shared_room',
  hotel_room: 'hotel_room',
};

const MapFilter = ({
  toggleDrawer,
  filterVal,
  setFilterVal,
  applyFilter,
  filterActive,
  setFilterActive,
  isDisabled,
  handleOnSampleClick,
  listingsCount,
  setZoom,
  onlyShowZoom,
  onZoomInClick,
  dashboardLayoutView,
  noDataInMarket,
}: {
  toggleDrawer: () => void;
  filterVal: FilterVal;
  setFilterVal: React.Dispatch<React.SetStateAction<FilterVal>>;
  filterActive: boolean;
  applyFilter;
  setFilterActive: React.Dispatch<React.SetStateAction<boolean>>;
  isDisabled: boolean;
  handleOnSampleClick: any;
  listingsCount: number;
  setZoom: React.Dispatch<React.SetStateAction<number>>;
  onlyShowZoom: boolean;
  onZoomInClick: () => void;
  dashboardLayoutView: 'map' | 'dashboard' | 'discovery' | 'compare' | 'advance' | 'searchListing';
  noDataInMarket: boolean;
}) => {
  const [selectedBedrooms, setSelectedBedrooms] = useState<string[]>(['Any']);
  let addRoomType: string[] = [];

  const isDataPresentToUseFilter: boolean = listingsCount > 0;

  const updateSelectedBedrooms = (bedroom: string) => {
    let updatedBedrooms = [...selectedBedrooms];
    if (updatedBedrooms.includes(bedroom)) {
      updatedBedrooms.splice(updatedBedrooms.indexOf(bedroom), 1);
    } else {
      updatedBedrooms.push(bedroom);
    }
    if (bedroom === 'Any') {
      updatedBedrooms = ['Any'];
    } else if (bedroom !== 'Any' && updatedBedrooms.includes('Any')) {
      updatedBedrooms.splice(updatedBedrooms.indexOf('Any'), 1);
    }

    if (!filterVal.roomType.includes('entire_home') && !filterVal.roomType.includes('condo')) {
      addRoomType = ['entire_home', 'condo'];
    }

    setSelectedBedrooms(updatedBedrooms);
    return updatedBedrooms;
  };

  useEffect(() => {
    if (filterActive) {
      setSelectedBedrooms(
        filterVal.bedrooms.map(bedroom => findKeyByValue(bedroomLabelToIndexMap, bedroom) as string)
      );
    } else {
      setSelectedBedrooms(['Any']);
    }
  }, [filterActive, filterVal]);

  const handleBedroomButtonClick = (bedroom: string) => {
    setFilterVal((prevFilterVal: FilterVal) => {
      let updatedBedrooms = updateSelectedBedrooms(bedroom);

      const newFilterVal = {
        ...prevFilterVal,
        bedrooms: updatedBedrooms.map(selectedBedroom => bedroomLabelToIndexMap[selectedBedroom]),
        roomType: [...prevFilterVal.roomType, ...addRoomType],
      };
      return newFilterVal;
    });
    const filtersToDo = ['bedrooms'];
    addRoomType.length > 0 && filtersToDo.push('roomType');

    applyFilter(undefined, false, filtersToDo);

    addRoomType = [];
    setFilterActive(true);
  };

  return (
    <Box className="mapFilterContainer">
      <Grid container 
      sx={{ transform: 'scale(0.5)', transformOrigin: 'bottom right' }}
      >
        <Grid container item xs={12} justifyContent={'flex-end'}>
          <ZoomButtons
            size={dashboardLayoutView === 'dashboard' ? 100 : 80}
            setZoom={setZoom}
            zoomInAction={() => onZoomInClick()}
          />
        </Grid>
        {!onlyShowZoom && (
          <>
            <Grid container item xs={12} justifyContent={'flex-end'}>
              <Box className="mapFilterInfoBox">
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 800, textShadow: '0 0 10px #fff', fontSize: '26px' }}
                >
                  Listing Filter{' '}
                  {isDisabled ? <LockSharpIcon style={{ fontSize: 27, color: 'inherit' }} /> : ''}
                </Typography>
              </Box>
            </Grid>
            <Grid container item xs={12} spacing={1} justifyContent={'flex-end'}>
              {['Any', 'Studio', '1', '2', '3', '4', '5', '6+'].map((bedroom, index) => (
                <Grid item key={'filter-bedroom-' + bedroom}>
                  <Button
                    className={
                      selectedBedrooms.includes(bedroom)
                        ? 'mapFilterButton--active'
                        : 'mapFilterButton'
                      // filterVal.bedrooms.includes(bedroomLabelToIndexMap[bedroom]) ? 'mapFilterButton--active' : 'mapFilterButton'
                    }
                    onClick={() =>
                      isDisabled ? handleOnSampleClick() : handleBedroomButtonClick(bedroom)
                    }
                    disabled={!isDataPresentToUseFilter ? true : false}
                  >
                    <span>{isDisabled ? <LockSharpIcon style={{ marginRight: '2px' }} /> : <></>}</span>
                    <span className={index > 2 ? "notranslate" : "" }>{bedroom}</span>
                  </Button>
                </Grid>
              ))}
              <Grid item>
                <Button
                  className="mapFilterButton"
                  onClick={() => toggleDrawer()}
                  // disabled={isDisabled ? true : false}
                  disabled={!isDataPresentToUseFilter ? true : false}
                >
                  <FilterAltOutlinedIcon sx={{ fontSize: 34 }} />
                  All Filters
                </Button>
              </Grid>
              {!isDataPresentToUseFilter && !noDataInMarket && (
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '85%',
                    left: '45%',
                    fontSize: '37px',
                    fontWeight: 900,
                    color: COLOR_E,
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                  }}
                >
                  Loading <CircularProgress sx={{ color: COLOR_E }} />
                </Typography>
              )}
              {noDataInMarket && (
                <Typography
                  sx={{
                    position: 'absolute',
                    top: '85%',
                    left: '45%',
                    fontSize: '37px',
                    fontWeight: 900,
                    color: COLOR_E,
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                  }}
                >
                  No Data Available
                </Typography>
              )}
            </Grid>
          </>
        )}
      </Grid>
    </Box>
  );
};

export default MapFilter;
